import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getAvatarSrc } from "@/components/user-avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ClipboardList, Users, History, Settings, ExternalLink } from "lucide-react";
import { type ActivityRecord } from "@/components/tasks/activity-feed";

export interface BoardSummary {
  id: string;
  name: string;
  description: string | null;
  type?: string;
  createdAt: string;
  openTaskCount: number;
  totalTaskCount: number;
  isArchived?: boolean;
  members: Array<{
    userId: string;
    role: string;
    username: string;
    firstName: string | null;
    lastName: string | null;
    avatarUrl: string | null;
  }>;
  latestActivity: ActivityRecord[];
}

interface MyBoardsGridProps {
  boards: BoardSummary[];
  isLoading: boolean;
  onSelectBoard: (boardId: string) => void;
  columns?: number;
  maxBoards?: number;
  showActivity?: boolean;
  onViewActivity?: (boardId: string) => void;
  emptyMessage?: string;
  currentUserId?: string;
  isCurrentUserAdmin?: boolean;
  adminMode?: boolean;
  onManageBoard?: (boardId: string) => void;
  onViewBoard?: (boardId: string, boardType: string) => void;
  onManageMembers?: (boardId: string) => void;
}

function getInitials(firstName: string | null, lastName: string | null, username: string): string {
  if (firstName && lastName) return (firstName[0] + lastName[0]).toUpperCase();
  if (firstName) return firstName.slice(0, 2).toUpperCase();
  return username.slice(0, 2).toUpperCase();
}

function getDisplayName(firstName: string | null, lastName: string | null, username: string): string {
  if (firstName && lastName) return `${firstName} ${lastName}`;
  if (firstName) return firstName;
  return username;
}

const roleLabels: Record<string, string> = {
  admin: "Admin",
  editor: "Editor",
  viewer: "Viewer",
};

function BoardCardSkeleton() {
  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <Skeleton className="h-5 w-3/4" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-1/2" />
        <div className="flex items-center justify-between pt-2">
          <Skeleton className="h-4 w-20" />
          <div className="flex -space-x-2">
            <Skeleton className="h-6 w-6 rounded-full" />
            <Skeleton className="h-6 w-6 rounded-full" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AdminBoardCard({
  board,
  onManageBoard,
  onViewBoard,
  onManageMembers,
}: {
  board: BoardSummary;
  onManageBoard?: (boardId: string) => void;
  onViewBoard?: (boardId: string, boardType: string) => void;
  onManageMembers?: (boardId: string) => void;
}) {
  const isArchived = board.isArchived ?? false;

  return (
    <Card
      className={isArchived ? "opacity-60" : ""}
      data-testid={`admin-board-card-${board.id}`}
    >
      <CardContent className="p-4">
        <p className="text-xs font-medium text-muted-foreground mb-1.5" data-testid={`text-admin-manage-banner-${board.id}`}>
          Manage {board.name}
        </p>
        <div className="flex items-center gap-2">
          <h3 className="font-medium text-foreground truncate flex-1" data-testid={`text-admin-board-name-${board.id}`}>
            {board.name}
          </h3>
          {isArchived && (
            <Badge variant="outline" className="text-[10px] shrink-0" data-testid={`badge-archived-${board.id}`}>
              Archived
            </Badge>
          )}
          {board.type && board.type !== "regular" && (
            <Badge variant="secondary" className="text-[10px] shrink-0 capitalize" data-testid={`badge-board-type-${board.id}`}>
              {board.type}
            </Badge>
          )}
        </div>

        {board.description && (
          <p className="text-sm text-muted-foreground mt-1 line-clamp-2" data-testid={`text-admin-board-desc-${board.id}`}>
            {board.description}
          </p>
        )}

        <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t">
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <ClipboardList className="h-3.5 w-3.5 flex-shrink-0" />
            <span data-testid={`text-admin-board-counts-${board.id}`}>
              {board.openTaskCount} open / {board.totalTaskCount} total
            </span>
          </div>

          {board.members.length > 0 && (
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Users className="h-3.5 w-3.5 flex-shrink-0" />
              <span data-testid={`text-admin-member-count-${board.id}`}>
                {board.members.length} {board.members.length === 1 ? "member" : "members"}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center flex-wrap gap-2 mt-3 pt-3 border-t">
          {onManageBoard && (
            <Button
              variant="default"
              size="sm"
              onClick={() => onManageBoard(board.id)}
              data-testid={`button-manage-board-${board.id}`}
            >
              <Settings className="h-3.5 w-3.5 mr-1.5" />
              Manage
            </Button>
          )}
          {onViewBoard && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onViewBoard(board.id, board.type || "regular")}
              data-testid={`button-view-board-${board.id}`}
            >
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
              View Board
            </Button>
          )}
          {onManageMembers && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onManageMembers(board.id)}
              data-testid={`button-manage-members-${board.id}`}
            >
              <Users className="h-3.5 w-3.5 mr-1.5" />
              Members
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function MyBoardsGrid({
  boards,
  isLoading,
  onSelectBoard,
  columns = 3,
  maxBoards,
  showActivity = false,
  onViewActivity,
  emptyMessage = "You are not a member of any boards yet.",
  currentUserId,
  isCurrentUserAdmin = false,
  adminMode = false,
  onManageBoard,
  onViewBoard,
  onManageMembers,
}: MyBoardsGridProps) {
  if (isLoading) {
    return (
      <div
        className={`grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-${columns}`}
        data-testid="my-boards-grid"
      >
        <BoardCardSkeleton />
        <BoardCardSkeleton />
        <BoardCardSkeleton />
      </div>
    );
  }

  if (boards.length === 0) {
    return (
      <div className="text-center py-8" data-testid="my-boards-grid">
        <Users className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
      </div>
    );
  }

  const displayBoards = maxBoards ? boards.slice(0, maxBoards) : boards;

  const gridColsClass =
    columns === 2 ? "grid-cols-1 sm:grid-cols-2" :
    columns === 4 ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4" :
    "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3";

  if (adminMode) {
    return (
      <div
        className={`grid gap-4 ${gridColsClass}`}
        data-testid="my-boards-grid"
      >
        {displayBoards.map(board => (
          <AdminBoardCard
            key={board.id}
            board={board}
            onManageBoard={onManageBoard}
            onViewBoard={onViewBoard}
            onManageMembers={onManageMembers}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      className={`grid gap-4 ${gridColsClass}`}
      data-testid="my-boards-grid"
    >
      {displayBoards.map(board => (
        <Card
          key={board.id}
          className="cursor-pointer hover-elevate transition-colors"
          onClick={() => onSelectBoard(board.id)}
          data-testid={`my-board-card-${board.id}`}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-foreground truncate flex-1" data-testid={`text-board-name-${board.id}`}>
                {board.name}
              </h3>
              {(() => {
                const myRole = isCurrentUserAdmin ? "admin" : currentUserId ? board.members.find(m => m.userId === currentUserId)?.role : null;
                return myRole ? (
                  <Badge variant="secondary" className="text-[10px] shrink-0" data-testid={`badge-board-role-${board.id}`}>
                    {roleLabels[myRole] || myRole}
                  </Badge>
                ) : null;
              })()}
            </div>
            {board.description && (
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2" data-testid={`text-board-desc-${board.id}`}>
                {board.description}
              </p>
            )}

            <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t">
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <ClipboardList className="h-3.5 w-3.5 flex-shrink-0" />
                <span data-testid={`text-board-counts-${board.id}`}>
                  {board.openTaskCount} open / {board.totalTaskCount} total
                </span>
              </div>

              {board.members.length > 0 && (
                <div className="flex -space-x-1.5 flex-shrink-0">
                  {board.members.slice(0, 4).map(member => (
                    <Tooltip key={member.userId}>
                      <TooltipTrigger asChild>
                        <Avatar className="h-6 w-6 border-2 border-background">
                          {member.avatarUrl && (
                            <AvatarImage src={getAvatarSrc(member.avatarUrl, member.userId)} alt={getDisplayName(member.firstName, member.lastName, member.username)} />
                          )}
                          <AvatarFallback className="text-[10px]">
                            {getInitials(member.firstName, member.lastName, member.username)}
                          </AvatarFallback>
                        </Avatar>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        <p className="text-xs">
                          {getDisplayName(member.firstName, member.lastName, member.username)}
                          {" "}<span className="text-muted-foreground">({roleLabels[member.role] || member.role})</span>
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  ))}
                  {board.members.length > 4 && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Avatar className="h-6 w-6 border-2 border-background">
                          <AvatarFallback className="text-[10px]">
                            +{board.members.length - 4}
                          </AvatarFallback>
                        </Avatar>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        <p className="text-xs">{board.members.length - 4} more members</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
              )}
            </div>

            {showActivity && onViewActivity && (
              <div className="mt-3 pt-3 border-t" data-testid={`board-activity-section-${board.id}`}>
                <button
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
                  onClick={(e) => { e.stopPropagation(); onViewActivity(board.id); }}
                  data-testid={`link-view-activity-${board.id}`}
                >
                  <History className="h-3 w-3" />
                  View Activity
                </button>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
